import { caseStudies, CaseStudyCategory } from '../content/caseStudies';
import CaseStudyCard from './CaseStudyCard';
import './CaseStudyGrid.css';

interface CaseStudyGridProps {
  /** Only show case studies with this category. Defaults to 'ux'. */
  category?: CaseStudyCategory;
  /** DOM id for the section (used for in-page anchor links). */
  id?: string;
  /** Small uppercase label above the heading. */
  eyebrow?: string;
  /** Section heading. */
  heading?: string;
  /** Optional sub-heading paragraph. */
  sub?: string;
}

/**
 * Renders a grid of case study cards, filtered by category. Used twice on
 * the homepage — once for the main UX work, once for the Creative Archive.
 */
export default function CaseStudyGrid({
  category = 'ux',
  id = 'case-studies',
  eyebrow = 'Selected work',
  heading = 'Recent case studies.',
  sub = 'A snapshot of recent work — every brief is different, but the goal is always shippable design with measurable impact.',
}: CaseStudyGridProps) {
  // Treat missing category as 'ux' (back-compat with older entries)
  const studies = caseStudies.filter(
    (s) => (s.category ?? 'ux') === category
  );

  // If there's nothing in the category, render nothing (keeps homepage clean)
  if (studies.length === 0) return null;

  const headingId = `${id}-heading`;

  return (
    <section
      id={id}
      className="case-grid section"
      aria-labelledby={headingId}
    >
      <div className="container">
        <div className="case-grid__head">
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          <h2 id={headingId} className="case-grid__heading display">
            {heading}
          </h2>
          {sub && <p className="case-grid__sub">{sub}</p>}
        </div>
        <ul className="case-grid__list" role="list">
          {studies.map((study) => (
            <li key={study.slug}>
              <CaseStudyCard study={study} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
